import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Center,
  Spinner,
  SimpleGrid,
  Text,
  Flex,
  Field,
  Dialog,
  Portal,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { HiExclamationTriangle, HiPlus } from 'react-icons/hi2';
import { FaRegEdit } from 'react-icons/fa';
import {
  DataTable,
  type Column,
  ToggleButton,
  SelectInput,
  SectionCard,
  DrawerFormFooter,
  InputField,
  Tooltip,
} from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { Button as ButtonComponent } from '@themes/aegix';
import { Link as RouterLink } from 'react-router-dom';
import {
  useBuildings,
  useCreateBuilding,
} from '../../../hooks/queries/useBuildings';
import { useUpdateSite, useDeleteSite } from '../../../hooks/queries/useSites';
import {
  useOrganizations,
  useSchoolPreselectedProviderOrgs,
} from '../../../hooks/queries';
import { PerSiteDrawer } from '../../forms/PerSiteDrawer';

import {
  useEntitlements,
  useLicenses,
  useSiteModuleOverrides,
  useSetSiteModuleOverride,
} from '../../../hooks/queries/useLicensing';
import { useHasPermissions } from '../../../utils/hasPermission';
import type { Building, Floor, Room } from '../../../services';
import type { SiteRow } from '../SitesFloorPlans';
import { BuildingDrawer } from './BuildingDrawer';
import { BlueprintsSection } from './BlueprintsSection';
import { toaster } from '../../ui/toaster';

const MAPS_MODULE = 'maps';
const AIM_PRODUCT = 'aim';
const MANAGE_LICENSE = ['licensing.manage_license'];
const DELETE_SCHOOL = ['schools.delete_school'];

interface BuildingRow {
  [key: string]: unknown;
  id: string;
  name: string;
}

const columns: Column<BuildingRow>[] = [
  { key: 'name', label: 'Building', sortable: true, width: '250px' },
];

function transformBuildings(buildings: Building[]): BuildingRow[] {
  return buildings.map((b) => ({
    id: String(b.id),
    name: b.name || b.building,
  }));
}

/**
 * License-aware Maps site toggle (ADR 104 §18.8). Three render paths:
 *  1. Org has no License at all → informational (Maps available, full access).
 *  2. Org has a License with Maps enabled → real toggle that writes
 *     `SiteModuleOverride` rows when flipped.
 *  3. Org has a License but Maps NOT enabled → upgrade CTA. Managers see
 *     a link to the Billing tab where they can flip the module on at the
 *     license level.
 */
function SiteLicensingSection({
  siteId,
  canManage,
}: {
  siteId: number;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const { data: entitlements, isLoading: entLoading } = useEntitlements();
  const { data: licensesData, isLoading: licLoading } = useLicenses({
    page_size: 50,
  });
  const aimLicense = useMemo(
    () =>
      (licensesData?.results ?? []).find((l) => l.product_code === AIM_PRODUCT),
    [licensesData],
  );
  const { data: overrides } = useSiteModuleOverrides(
    aimLicense?.id ?? 0,
    siteId,
  );
  const setOverride = useSetSiteModuleOverride();

  const loading = entLoading || licLoading;
  const unlicensed = !loading && entitlements?.status === 'unlicensed';
  const mapsEnabledOnLicense =
    !!entitlements && entitlements.modules?.[MAPS_MODULE] === true;
  const mapsOverride = (overrides ?? []).find(
    (o) => o.module_code === MAPS_MODULE,
  );
  const siteMapsActive = mapsOverride
    ? mapsOverride.enabled
    : mapsEnabledOnLicense;

  if (loading) {
    return (
      <SectionCard title={t('sites.licensing', 'Licensing')}>
        <Center py={3}>
          <Spinner size="sm" />
        </Center>
      </SectionCard>
    );
  }

  // Case 1: org isn't in the licensing system at all (per-org gate fallback).
  if (unlicensed) {
    return (
      <SectionCard title={t('sites.licensing', 'Licensing')}>
        <Text fontSize="sm" color="text.secondary">
          {t(
            'sites.unlicensedNotice',
            'This organization is not in the licensing system, so all modules including Maps are available. Floorplans uploaded here will be processed by the map provider.',
          )}
        </Text>
      </SectionCard>
    );
  }

  // Case 3: License exists but Maps module not enabled — CTA, no toggle.
  if (!mapsEnabledOnLicense) {
    return (
      <SectionCard title={t('sites.licensing', 'Licensing')}>
        <Text fontSize="sm" color="text.primary" mb={2}>
          {t(
            'sites.mapsNotInPlan',
            "Maps module isn't in this organization's plan.",
          )}
        </Text>
        <Text fontSize="xs" color="text.tertiary" mb={3}>
          {t(
            'sites.mapsUpgradeNotice',
            'Uploaded blueprints are served as static images on AIM. Add the Maps module to enable dynamic map-provider processing.',
          )}
        </Text>
        <RouterLink to="/account?tab=billing">
          <ButtonComponent visual="secondary" size="sm">
            {canManage
              ? t('sites.enableMapsOnLicense', 'Enable Maps module on license')
              : t('sites.viewBilling', 'View billing')}
          </ButtonComponent>
        </RouterLink>
      </SectionCard>
    );
  }

  // Case 2: Maps enabled on the org's License → real toggle. Flipping
  // writes a SiteModuleOverride row for this (license, site, maps) triple.
  const handleToggle = async () => {
    if (!aimLicense) return;
    await setOverride.mutateAsync({
      licenseId: aimLicense.id,
      siteId,
      module_code: MAPS_MODULE,
      enabled: !siteMapsActive,
    });
  };

  return (
    <SectionCard title={t('sites.licensing', 'Licensing')}>
      <ToggleButton
        e2eTitle="licensed"
        name="licensed"
        title={t('sites.mapsEnabledAtSite', 'Maps enabled at this site')}
        checked={siteMapsActive}
        onChange={handleToggle}
        disabled={setOverride.isPending || !aimLicense}
      />
      <Text fontSize="xs" color="text.tertiary" mt={1}>
        {siteMapsActive
          ? t(
              'sites.licensedHelperTrue',
              'Uploaded blueprints will be processed by the map provider to generate dynamic maps.',
            )
          : t(
              'sites.mapsDisabledOverrideNotice',
              'Maps is disabled for this site via override. Uploaded blueprints will be served as static images.',
            )}
      </Text>
      {setOverride.isError && (
        <Text fontSize="xs" color="text.danger" mt={1}>
          {(setOverride.error as Error)?.message ??
            t('sites.failedUpdateOverride', 'Failed to update site override')}
        </Text>
      )}
    </SectionCard>
  );
}

interface SiteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  site: SiteRow | null;
  selectedBuilding: Building | null;
  onSelectBuilding: (building: Building | null) => void;
  selectedFloor: Floor | null;
  onSelectFloor: (floor: Floor | null) => void;
  selectedRoom: Room | null;
  onSelectRoom: (room: Room | null) => void;
}

import { useTranslation } from 'react-i18next';

export function SiteDrawer({
  isOpen,
  onClose,
  site,
  selectedBuilding,
  onSelectBuilding,
  selectedFloor,
  onSelectFloor,
  selectedRoom,
  onSelectRoom,
}: SiteDrawerProps) {
  const { t } = useTranslation();

  const columns: Column<BuildingRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('sites.building', 'Building'),
        sortable: true,
        width: '250px',
      },
    ],
    [t],
  );
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [siteAddress2, setSiteAddress2] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [siteState, setSiteState] = useState('');
  const [siteZip, setSiteZip] = useState('');
  const [siteCountry, setSiteCountry] = useState('');
  const [sitePhone, setSitePhone] = useState('');
  const [siteLatitude, setSiteLatitude] = useState('');
  const [siteLongitude, setSiteLongitude] = useState('');
  const [siteElevation, setSiteElevation] = useState('');
  const [drillEnabled, setDrillEnabled] = useState(true);
  const [showSafeUnsafe, setShowSafeUnsafe] = useState(true);
  const [orgPublicId, setOrgPublicId] = useState('');
  const { data: orgData = [], isError: orgError } = useOrganizations();
  const orgOptions = orgData.map((o) => ({
    label: o.label,
    value: o.publicId,
    districtId: o.id,
  }));
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const canManageLicense = useHasPermissions(MANAGE_LICENSE);
  const canDeleteSchool = useHasPermissions(DELETE_SCHOOL);
  const deleteSiteMutation = useDeleteSite();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPerSiteDrawerOpen, setIsPerSiteDrawerOpen] = useState(false);
  const { data: preselectedData, isLoading: isLoadingPreselected } =
    useSchoolPreselectedProviderOrgs(site?.id ? String(site.id) : '');

  const { data, isLoading, error } = useBuildings({
    school: site ? Number(site.id) : undefined,
    page_size: 50,
  });
  const createMutation = useCreateBuilding();
  const updateSiteMutation = useUpdateSite();

  const allRows = data ? transformBuildings(data.results) : [];
  const rows = useMemo(() => {
    if (!search) return allRows;
    const q = search.toLowerCase();
    return allRows.filter((r) => r.name.toLowerCase().includes(q));
  }, [allRows, search]);

  useEffect(() => {
    if (site) {
      setSiteName(site.name);
      setSiteAddress(site.raw_address ?? '');
      setSiteAddress2(site.address_2 ?? '');
      setSiteCity(site.raw_city ?? '');
      setSiteState(site.state ?? '');
      setSiteZip(site.zip ?? '');
      setSiteCountry(site.country ?? '');
      setSitePhone(site.phone_number ?? '');
      setSiteLatitude(site.latitude ?? '');
      setSiteLongitude(site.longitude ?? '');
      setSiteElevation(site.elevation ?? '');
      setDrillEnabled(site.drill_enabled ?? true);
      setShowSafeUnsafe(site.show_safe_unsafe ?? true);
      setOrgPublicId(site.organization_public_id ?? '');
      setDirty(false);
    }
  }, [site]);

  const handleClose = useCallback(() => {
    onSelectBuilding(null);
    onSelectFloor(null);
    onSelectRoom(null);
    onClose();
  }, [onClose, onSelectBuilding, onSelectFloor, onSelectRoom]);

  const handleRowClick = useCallback(
    (row: BuildingRow) => {
      const building = data?.results.find((b) => String(b.id) === row.id);
      if (building) {
        onSelectBuilding(building);
        onSelectFloor(null);
        onSelectRoom(null);
      }
    },
    [data, onSelectBuilding, onSelectFloor, onSelectRoom],
  );

  const handleAddBuilding = useCallback(() => {
    if (!site) return;
    createMutation.mutate(
      {
        name: 'New Building',
        building: 'New Building',
        school: site.id,
        organization: '',
      },
      {
        onSuccess: () => {
          toaster.create({
            title: 'Building created',
            type: 'success',
            duration: 3000,
          });
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description:
              err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [site, createMutation]);

  const handleSaveSite = useCallback(() => {
    if (!site || !siteName.trim()) return;
    const selectedOrg = orgOptions.find((o) => o.value === orgPublicId);
    updateSiteMutation.mutate(
      {
        id: Number(site.id),
        data: {
          name: siteName.trim(),
          address: siteAddress.trim(),
          address_2: siteAddress2.trim(),
          city: siteCity.trim(),
          state: siteState.trim(),
          zip: siteZip.trim(),
          country: siteCountry.trim(),
          phone_number: sitePhone.trim(),
          latitude: siteLatitude.trim() || undefined,
          longitude: siteLongitude.trim() || undefined,
          elevation: siteElevation.trim() || undefined,
          drill_enabled: drillEnabled,
          show_safe_unsafe: showSafeUnsafe,
          ...(selectedOrg ? { district: selectedOrg.districtId } : {}),
        },
      },
      {
        onSuccess: () => {
          toaster.create({
            title: 'Site updated',
            type: 'success',
            duration: 3000,
          });
          setDirty(false);
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description:
              err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [
    site,
    siteName,
    siteAddress,
    siteAddress2,
    siteCity,
    siteState,
    siteZip,
    siteCountry,
    sitePhone,
    siteLatitude,
    siteLongitude,
    siteElevation,
    drillEnabled,
    showSafeUnsafe,
    orgPublicId,
    orgOptions,
    updateSiteMutation,
  ]);

  const handleDeleteSite = useCallback(() => {
    if (!site) return;
    deleteSiteMutation.mutate(Number(site.id), {
      onSuccess: () => {
        toaster.create({
          title: 'Site deleted',
          type: 'success',
          duration: 3000,
        });
        setIsDeleteDialogOpen(false);
        handleClose();
      },
      onError: (err: unknown) => {
        toaster.create({
          title: 'Error',
          description:
            err instanceof Error ? err.message : 'Failed to delete site',
          type: 'error',
          duration: 4000,
        });
      },
    });
  }, [site, deleteSiteMutation, handleClose]);

  return (
    <>
      <DrawerComponent
        isOpen={isOpen}
        onClose={handleClose}
        title={
          site
            ? `${t('common.edit', 'Edit')} ${site.name}`
            : t('sites.editSite', 'Edit Site')
        }
        e2eTitle="site-buildings-drawer"
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <DrawerFormFooter
            e2eTitle="site-buildings"
            onCancel={handleClose}
            cancelLabel={t('common.cancel', 'Cancel')}
            onSave={handleSaveSite}
            saveLabel={t('common.save', 'Save')}
            saveDisabled={!dirty}
            isLoading={updateSiteMutation.isPending}
            dangerAction={
              canDeleteSchool && site
                ? {
                    label: t('common.delete', 'Delete'),
                    onClick: () => setIsDeleteDialogOpen(true),
                  }
                : undefined
            }
          />
        }
      >
        <Flex direction="column" gap={4}>
          <SectionCard
            title={t('sites.orgIdentity', 'Organization & Identity')}
          >
            <Flex direction="column" gap={2}>
              <Box>
                <SelectInput
                  value={orgPublicId}
                  name="organization"
                  label={t('common.organization', 'Organization')}
                  option={orgOptions}
                  onChange={(val: string) => {
                    setOrgPublicId(val);
                    setDirty(true);
                  }}
                  data-e2e="site-organization-select"
                />
                {orgError && (
                  <Text fontSize="xs" color="text.danger" mt={1}>
                    {t(
                      'sites.failedLoadOrgs',
                      'Failed to load organizations. Please try refreshing.',
                    )}
                  </Text>
                )}
              </Box>
              <Field.Root>
                <Field.Label>{t('common.name', 'Name')}</Field.Label>
                <InputField
                  size="sm"
                  value={siteName}
                  onChange={(e) => {
                    setSiteName(e.target.value);
                    setDirty(true);
                  }}
                  data-e2e="site-name-input"
                />
              </Field.Root>
            </Flex>
          </SectionCard>

          <SectionCard
            title={t('sites.perSite', 'Per Site')}
            action={
              site
                ? (() => {
                    const hasProviderOrgs =
                      preselectedData?.results &&
                      preselectedData.results.length > 0;
                    const label = hasProviderOrgs
                      ? t(
                          'sites.editProviderOrgs',
                          'Edit provider organizations',
                        )
                      : t(
                          'sites.addProviderOrgs',
                          'Add provider organizations',
                        );
                    return (
                      <Tooltip content={label}>
                        <ButtonComponent
                          visual="secondary"
                          size="sm"
                          type="button"
                          aria-label={label}
                          onClick={() => setIsPerSiteDrawerOpen(true)}
                          data-e2e="add-per-site-provider-button"
                        >
                          {hasProviderOrgs ? (
                            <FaRegEdit size={14} />
                          ) : (
                            <HiPlus size={14} />
                          )}
                        </ButtonComponent>
                      </Tooltip>
                    );
                  })()
                : undefined
            }
          >
            {!site ? (
              <Text fontSize="xs" color="text.tertiary">
                {t(
                  'sites.saveSiteFirstForProviderOrgs',
                  'Save the site first to configure pre-selected provider organizations.',
                )}
              </Text>
            ) : isLoadingPreselected ? (
              <Flex align="center" gap={2} py={1}>
                <Spinner size="xs" color="button.primary" />
                <Text fontSize="xs" color="text.secondary">
                  {t('common.loading', 'Loading...')}
                </Text>
              </Flex>
            ) : !preselectedData?.results ||
              preselectedData.results.length === 0 ? (
              <Text fontSize="xs" color="text.secondary">
                {t(
                  'sites.noPreselectedProviderOrgs',
                  'No pre-selected provider organizations configured.',
                )}
              </Text>
            ) : (
              <VStack gap={2} align="stretch">
                {preselectedData.results.map((item) => (
                  <Flex
                    key={item.id}
                    justify="space-between"
                    align="center"
                    py={1.5}
                    px={3}
                    bg="surface.secondary"
                    border="1px solid"
                    borderColor="border.secondary"
                    borderRadius="sm"
                    data-e2e={`preselected-provider-item-${item.id}`}
                  >
                    <Text fontSize="sm" fontWeight={500} color="text.primary">
                      {item.provider_org_name}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </SectionCard>

          <SectionCard title={t('common.address', 'Address')}>
            <Flex direction="column" gap={2}>
              <Field.Root>
                <Field.Label>{t('common.address', 'Address')}</Field.Label>
                <InputField
                  size="sm"
                  value={siteAddress}
                  onChange={(e) => {
                    setSiteAddress(e.target.value);
                    setDirty(true);
                  }}
                  data-e2e="site-state-input"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('common.address_2', 'Address 2')}</Field.Label>
                <InputField
                  size="sm"
                  value={siteAddress2}
                  onChange={(e) => {
                    setSiteAddress2(e.target.value);
                    setDirty(true);
                  }}
                  data-e2e="site-zip-input"
                />
              </Field.Root>
              <SimpleGrid columns={2} gap={2}>
                <Field.Root>
                  <Field.Label>{t('common.city', 'City')}</Field.Label>
                  <InputField
                    size="sm"
                    value={siteCity}
                    onChange={(e) => {
                      setSiteCity(e.target.value);
                      setDirty(true);
                    }}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>{t('common.state', 'State')}</Field.Label>
                  <InputField
                    size="sm"
                    value={siteState}
                    onChange={(e) => {
                      setSiteState(e.target.value);
                      setDirty(true);
                    }}
                  />
                </Field.Root>
              </SimpleGrid>
              <SimpleGrid columns={2} gap={2}>
                <Field.Root>
                  <Field.Label>{t('common.zip', 'Zip')}</Field.Label>
                  <InputField
                    size="sm"
                    value={siteZip}
                    onChange={(e) => {
                      setSiteZip(e.target.value);
                      setDirty(true);
                    }}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>{t('common.country', 'Country')}</Field.Label>
                  <InputField
                    size="sm"
                    value={siteCountry}
                    onChange={(e) => {
                      setSiteCountry(e.target.value);
                      setDirty(true);
                    }}
                  />
                </Field.Root>
              </SimpleGrid>
              <Field.Root>
                <Field.Label>
                  {t('sites.phoneNumber', 'Phone Number')}
                </Field.Label>
                <InputField
                  size="sm"
                  value={sitePhone}
                  onChange={(e) => {
                    setSitePhone(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="+1 (555) 123-4567"
                  data-e2e="site-phone-input"
                />
              </Field.Root>
            </Flex>
          </SectionCard>

          <SectionCard title={t('sites.geolocation', 'Geolocation')}>
            <SimpleGrid columns={3} gap={2}>
              <Field.Root>
                <Field.Label>{t('sites.latitude', 'Latitude')}</Field.Label>
                <InputField
                  size="sm"
                  value={siteLatitude}
                  onChange={(e) => {
                    setSiteLatitude(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="0.000000"
                  data-e2e="site-lat-input"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('sites.longitude', 'Longitude')}</Field.Label>
                <InputField
                  size="sm"
                  value={siteLongitude}
                  onChange={(e) => {
                    setSiteLongitude(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="0.000000"
                  data-e2e="site-lng-input"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('sites.elevation', 'Elevation')}</Field.Label>
                <InputField
                  size="sm"
                  value={siteElevation}
                  onChange={(e) => {
                    setSiteElevation(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="0"
                  data-e2e="site-elevation-input"
                />
              </Field.Root>
            </SimpleGrid>
          </SectionCard>

          <SectionCard title={t('events.toggle.alerts', 'Alerts')}>
            <Flex direction="column" gap={2}>
              <ToggleButton
                e2eTitle="drill_enabled"
                name="drill_enabled"
                title={t('sites.showDrillButton', 'Show Drill Button')}
                checked={drillEnabled}
                onChange={() => {
                  setDrillEnabled(!drillEnabled);
                  setDirty(true);
                }}
              />
              <ToggleButton
                e2eTitle="show_safe_unsafe"
                name="show_safe_unsafe"
                title={t('sites.promptSafetyStatus', 'Prompt Safety Status')}
                checked={showSafeUnsafe}
                onChange={() => {
                  setShowSafeUnsafe(!showSafeUnsafe);
                  setDirty(true);
                }}
              />
            </Flex>
          </SectionCard>

          <PerSiteDrawer
            isOpen={isPerSiteDrawerOpen}
            onClose={() => setIsPerSiteDrawerOpen(false)}
            schoolId={site?.id}
            initialProviderOrgIds={preselectedData?.provider_org_ids}
          />

          {site && (
            <SiteLicensingSection
              siteId={Number(site.id)}
              canManage={canManageLicense}
            />
          )}

          {site && (
            <SectionCard title={t('sites.blueprints', 'Blueprints')}>
              <BlueprintsSection siteId={Number(site.id)} />
            </SectionCard>
          )}

          <SectionCard
            title={t('sites.buildings', 'Buildings')}
            action={
              <ButtonComponent
                visual="primary"
                size="sm"
                onClick={handleAddBuilding}
              >
                {t('sites.addBuilding', 'Add Building')}
              </ButtonComponent>
            }
          >
            <InputField
              size="sm"
              placeholder={t('sites.filterBuildings', 'Filter buildings...')}
              aria-label="Filter buildings"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              mb={3}
              data-e2e="filter-buildings-input"
            />

            {isLoading ? (
              <Center py={8}>
                <Spinner size="lg" color="button.primary" />
              </Center>
            ) : error ? (
              <Center py={8}>
                <Text color="text.danger">
                  {t('sites.failedLoadBuildings', 'Failed to load buildings')}
                </Text>
              </Center>
            ) : rows.length === 0 ? (
              <Center py={8}>
                <Text color="text.secondary">
                  {t('sites.noBuildingsFound', 'No buildings found')}
                </Text>
              </Center>
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                keyExtractor={(row) => row.id}
                onRowClick={handleRowClick}
              />
            )}
          </SectionCard>
        </Flex>
      </DrawerComponent>

      <BuildingDrawer
        isOpen={!!selectedBuilding}
        onClose={() => onSelectBuilding(null)}
        building={selectedBuilding}
        selectedFloor={selectedFloor}
        onSelectFloor={onSelectFloor}
        selectedRoom={selectedRoom}
        onSelectRoom={onSelectRoom}
      />

      <Dialog.Root
        open={isDeleteDialogOpen}
        onOpenChange={(details) => {
          if (!deleteSiteMutation.isPending)
            setIsDeleteDialogOpen(details.open);
        }}
        placement="center"
        size="sm"
        role="alertdialog"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              bgColor="surface.primary"
              borderRadius="lg"
              maxWidth="480px"
              data-e2e="delete-site-dialog"
            >
              <Dialog.Header
                p="20px 20px 8px 20px"
                display="flex"
                alignItems="center"
                gap={3}
              >
                <Box flexShrink={0} color="text.danger">
                  <HiExclamationTriangle size={28} />
                </Box>
                <Dialog.Title
                  fontSize="16px"
                  fontWeight={600}
                  color="text.primary"
                >
                  {t('sites.deleteSiteConfirm', 'Delete site?')}
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body p="0 20px 16px 20px">
                <Text fontSize="sm" color="text.secondary">
                  {t(
                    'sites.deleteConfirmNotice',
                    'Deleting {{name}} will also delete all buildings, floors, and rooms within it. This action cannot be undone.',
                    { name: site?.name ?? '' },
                  )}
                </Text>
              </Dialog.Body>

              <Box p="0 20px 20px 20px">
                <HStack gap={3} justify="flex-end">
                  <ButtonComponent
                    visual="secondary"
                    onClick={() => setIsDeleteDialogOpen(false)}
                    disabled={deleteSiteMutation.isPending}
                    data-e2e="delete-site-cancel"
                  >
                    {t('common.cancel', 'Cancel')}
                  </ButtonComponent>
                  <ButtonComponent
                    visual="alert"
                    onClick={handleDeleteSite}
                    disabled={deleteSiteMutation.isPending}
                    data-e2e="delete-site-confirm"
                  >
                    {deleteSiteMutation.isPending
                      ? t('sites.deleting', 'Deleting...')
                      : t('sites.deleteSite', 'Delete Site')}
                  </ButtonComponent>
                </HStack>
              </Box>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
